import type { DateTime, LocalDateTime, LocalTime, Time } from 'neo4j-driver';
import type { Node as Neo4jNode, Relationship as Neo4jRelationship } from 'neo4j-driver-core';

import type { Nullable } from './utility';

export type RelationshipDirection = 'incoming' | 'outgoing';

export interface MappedProperty {
  type: string;
  value: string | number | boolean;
}

export type PropertyValueTypes =
  | number
  | bigint
  | string
  | boolean
  | Date
  | LocalTime
  | LocalDateTime
  | Time
  | DateTime;

export type PropertyTypeStringRepresentation =
  | 'Date'
  | 'DateTime'
  | 'Duration'
  | 'LocalDateTime'
  | 'LocalTime'
  | 'Point'
  | 'Time'
  | 'array'
  | 'bigint'
  | 'boolean'
  | 'function'
  | 'number'
  | 'object'
  | 'string'
  | 'symbol';

export interface Node extends Neo4jNode {
  entityType: 'Node';
  labelString: string;
  id: string;
  pinned?: boolean;
  placement?: {
    x: number;
    y: number;
    center?: string;
  };
  mappedProperties: Nullable<Record<string, Nullable<MappedProperty>>>;
}

export interface NodeGroup {
  entityType: 'NodeGroup';
  labelStrings: string[];
  id: string;
  groupedIds: string[];
  pinned?: boolean;
  placement?: {
    x: number;
    y: number;
    center?: string;
  };
}

export function isNodeGroup(node: Node | NodeGroup): node is NodeGroup {
  return node.entityType === 'NodeGroup';
}

export interface NodePosition {
  id: Node['id'];
  x: number;
  y: number;
}

export interface Relationship extends Neo4jRelationship {
  entityType: 'Rel';
  endId: string;
  startId: string;
  id: string;
  mappedProperties: Nullable<Record<string, Nullable<MappedProperty>>>;
  // Re-declares 'properties' from neo4j-driver-core to avoid TypeScript complaining about 'Properties' type not being exported
  properties: Record<string, any>;
}

export interface RelationshipGroup {
  id: string;
  startId: Node['id'];
  endId: Node['id'];
  relationships: Relationship['id'][];
}

export function isRelationshipGroup(relationship: Relationship | RelationshipGroup): relationship is RelationshipGroup {
  return (relationship as RelationshipGroup).relationships !== undefined;
}
